import { useMemo, useState } from 'react';
import { Activity } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardBody, Button } from '@vnetwork/ui/primitives';
import { Section } from '@vnetwork/ui/layout';
import { EmptyState } from '@vnetwork/ui/data';
import { Chart } from '@vnetwork/ui/charts';
import { useInstanceMonitoring, type MonitoringPoint } from '@/api/opennebula';
import { DateRangePicker, rangeFor } from '../components/DateRangePicker';

/** Minimal shape the chart shaping needs — satisfied by both Instance and
 *  LoadBalancer, so the same tab renders metrics for either (both are VMs). */
type MonitoredResource = { id: string; metadata?: { resources?: { cores?: number } } };

const num = (v: string | number | undefined) => (v == null ? 0 : Number(v));

/** Cumulative counter → per-sample positive delta. */
function deltas(points: MonitoringPoint[], pick: (p: MonitoringPoint) => number, divisor: number) {
  const raw = points.map((p) => pick(p) / divisor);
  return raw.map((v, i) => {
    const d = i === 0 ? 0 : v - raw[i - 1];
    return Number((d < 0 ? 0 : d).toFixed(2));
  });
}

function hhmmss(ts: string | number): string {
  const d = new Date(num(ts) * 1000);
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

export function MonitoringTab({
  serviceId,
  instance,
  useMonitoring = useInstanceMonitoring,
  description = 'CPU, memory, disk and network for this instance.',
}: {
  serviceId: string;
  instance: MonitoredResource;
  /** Metrics source — defaults to the instance endpoint; pass the LB hook to
   *  render the same charts for a load balancer's VRouter VM. */
  useMonitoring?: typeof useInstanceMonitoring;
  description?: string;
}) {
  const [preset, setPreset] = useState('1h');
  const range = useMemo(() => rangeFor(preset), [preset]);
  const { data: points = [], isLoading, isError, refetch } = useMonitoring(serviceId, instance.id, range);

  const cores = instance.metadata?.resources?.cores || 1;

  const charts = useMemo(() => {
    const t = points.map((p) => hhmmss(p.TIMESTAMP));
    const cpu = points.map((p) => Number(((num(p.CPU) / (cores * 1000)) * 100).toFixed(2)));
    const mem = points.map((p) => Number((num(p.MEMORY) / 1024).toFixed(2)));
    const rdiops = deltas(points, (p) => num(p.DISKRDIOPS), 30);
    const wriops = deltas(points, (p) => num(p.DISKWRIOPS), 30);
    const netrx = deltas(points, (p) => num(p.NETRX), 1024);
    const nettx = deltas(points, (p) => num(p.NETTX), 1024);
    const rows = (vals: number[]) => t.map((time, i) => ({ t: time, v: vals[i] ?? 0 }));
    return [
      { title: 'CPU usage', unit: '%', data: rows(cpu) },
      { title: 'Memory', unit: 'MB', data: rows(mem) },
      { title: 'Disk read', unit: 'IOPS', data: rows(rdiops) },
      { title: 'Disk write', unit: 'IOPS', data: rows(wriops) },
      { title: 'Network in', unit: 'Kbps', data: rows(netrx) },
      { title: 'Network out', unit: 'Kbps', data: rows(nettx) },
    ];
  }, [points, cores]);

  return (
    <Section
      title="Monitoring"
      description={description}
      actions={<DateRangePicker value={preset} onChange={setPreset} onRefresh={() => refetch()} />}
    >
      {isError ? (
        <EmptyState
          icon={<Activity size={28} />}
          title="Couldn't load metrics"
          description="Something went wrong fetching monitoring data for this instance."
          actions={
            <Button variant="secondary" onClick={() => refetch()}>
              Retry
            </Button>
          }
        />
      ) : !isLoading && points.length === 0 ? (
        <EmptyState icon={<Activity size={28} />} title="No metrics" description="No monitoring samples for the selected range." />
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(360px, 100%), 1fr))', gap: 'var(--vn-space-4)' }}>
          {charts.map((c) => (
            <Card key={c.title}>
              <CardHeader>
                <CardTitle>
                  {c.title} <span style={{ color: 'var(--vn-fg-muted)', fontWeight: 400 }}>({c.unit})</span>
                </CardTitle>
              </CardHeader>
              <CardBody>
                <Chart
                  type="area"
                  data={c.data}
                  xKey="t"
                  series={[{ key: 'v', label: c.title }]}
                  height={200}
                  showLegend={false}
                />
              </CardBody>
            </Card>
          ))}
        </div>
      )}
    </Section>
  );
}
